import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];
const USE_CASES = ['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other'];
export function WatchGigaDemo() {
  useScreenInit();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [size, setSize] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  const [workflows, setWorkflows] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your work email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/talk-to-sales');
    }, 800);
  };
  return <div className="min-h-screen bg-[#1C1510] flex items-center justify-center py-24 px-6 relative overflow-hidden" data-id="element-13760" data-mp="3he5fgqwtmiag">
      {/* Background accents */}
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-13761" data-mp="3m66o82us5qvq" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-13762" data-mp="3r2x4orahyoho" />

      <div className="relative z-10 w-full max-w-2xl" data-id="element-13763" data-mp="4vystmjtz6w3">
        <AnimatePresence mode="wait" data-id="element-13764" data-mp="4udy9wxinny8">
          <motion.div key="form" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden" data-id="element-13765" data-mp="3bd7je5momxhm">
            <div className="p-8 md:p-10 border-b border-white/5" data-id="element-13766" data-mp="3gc2aduuszsct">
              <h1 className="font-title font-bold text-3xl text-white mb-3" data-id="element-13767" data-mp="3u4i7yg4xqy6c">
                Meet Giga, your team's new coworker.
              </h1>
              <p className="text-neutral-400 leading-relaxed" data-id="element-13768" data-mp="3p4m36eoez2pq">
                Tell us about your company and what you want to see, so we can
                prepare a personalized live demo. Mentioning specific tools
                helps Giga tailor the examples perfectly to your stack.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 md:p-10 space-y-8" data-id="element-13769" data-mp="29qrc6n1pi27p">
              {/* Email */}
              <div className="space-y-3" data-id="element-13770" data-mp="24rws7hs067m0">
                <label htmlFor="email" className="block text-sm font-bold text-white" data-id="element-13771" data-mp="k79joqyf74x8">
                  Your work email
                </label>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className={`w-full bg-black/20 border ${error ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#EA580C]'} rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-[#EA580C] transition-colors`} data-id="element-13772" data-mp="p8p3ygsrx7ek" />
                {error && <p className="text-red-400 text-sm flex items-center gap-1.5 mt-2" data-id="element-13773" data-mp="5gagjvgct8l1">
                    <AlertCircle className="w-4 h-4" data-id="element-13774" data-mp="3nqcn8rc8u0x3" />
                    {error}
                  </p>}
              </div>

              {/* Company Size */}
              <div className="space-y-3" data-id="element-13775" data-mp="1lxwts10fe2oa">
                <label htmlFor="size" className="block text-sm font-bold text-white" data-id="element-13776" data-mp="1lusjclwdaap6">
                  How many people work at your company?
                </label>
                <div className="relative" data-id="element-13777" data-mp="3mxtd7wcj0acn">
                  <select id="size" value={size} onChange={e => setSize(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white appearance-none focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors" data-id="element-13778" data-mp="3i006n27b9ebu">
                    <option value="" disabled className="bg-[#2B211A] text-neutral-500" data-id="element-13779" data-mp="391o72aogv679">
                      Select company size...
                    </option>
                    {COMPANY_SIZES.map(s => <option key={s} value={s} className="bg-[#2B211A]" data-id="element-13780" data-mp="2z87a4xt19azd">
                        {s}
                      </option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500" data-id="element-13781" data-mp="2q9n34twu4pqz">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-13782" data-mp="2l8ftsqf1nii7">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-13783" data-mp="2gbfxh77zxu4a" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Use Cases */}
              <div className="space-y-3" data-id="element-13784" data-mp="15qjjgi2z56fl">
                <label htmlFor="usecase" className="block text-sm font-bold text-white" data-id="element-13785" data-mp="15rktqkc61ow9">
                  What would Giga help you with? Select from use cases below:
                </label>
                <div className="relative" data-id="element-13786" data-mp="d3p23u1gen6c">
                  <select id="usecase" value={selectedUseCase} onChange={e => setSelectedUseCase(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white appearance-none focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors" data-id="element-13787" data-mp="i0yzpwyzy559">
                    <option value="" disabled className="bg-[#2B211A] text-neutral-500" data-id="element-13788" data-mp="3qhk1aspatvyc">
                      Select a use case...
                    </option>
                    {USE_CASES.map(uc => <option key={uc} value={uc} className="bg-[#2B211A]" data-id="element-13789" data-mp="11o5a04b2np2k">
                        {uc}
                      </option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500" data-id="element-13790" data-mp="wosg4zls9hqh">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-13791" data-mp="1bg1f3zbksfga">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-13792" data-mp="ip9l98hzg2w4" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Specific Workflows */}
              <div className="space-y-3" data-id="element-13793" data-mp="rrafrxlu0au3">
                <label htmlFor="workflows" className="block text-sm font-bold text-white" data-id="element-13794" data-mp="nom4lj9narty">
                  What specific workflows and tools should Giga handle?
                </label>
                <p className="text-xs text-neutral-400 mb-2" data-id="element-13795" data-mp="1z3j7nx2nzhkb">
                  List them and we'll prepare live examples for you.
                </p>
                <textarea id="workflows" value={workflows} onChange={e => setWorkflows(e.target.value)} placeholder="e.g. Pulling Stripe revenue into a weekly Slack report, or creating Linear issues from Zendesk tickets..." rows={4} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors resize-none" data-id="element-13796" data-mp="26m91gg8zt3yi" />
              </div>

              {/* Submit */}
              <div className="pt-4 border-t border-white/5 flex justify-end" data-id="element-13797" data-mp="2uc3ccs9r4ig4">
                <button type="submit" disabled={isSubmitting} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-3.5 rounded-xl font-bold transition-colors shadow-lg shadow-[#EA580C]/20 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-13798" data-mp="2fmp2lj29jz07">
                  {isSubmitting ? <span className="flex items-center gap-2" data-id="element-13799" data-mp="3d3dxstwpk30m">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-13800" data-mp="3d4yrkhb77hxf">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-13801" data-mp="1893glyrgozyi"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-13802" data-mp="1492ojbbfwq8z"></path>
                      </svg>
                      Preparing...
                    </span> : <>
                      Prepare my live demo
                      <ArrowRight className="w-4 h-4" data-id="element-13803" data-mp="1mz2b9rjt5t98" />
                    </>}
                </button>
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}